import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Bold, Italic, List, ListOrdered } from 'lucide-react'

interface Props {
  value: string
  onChange: (wert: string) => void
  disabled?: boolean
  rows?: number
  className?: string
}

/**
 * AP-32 9.2 — **Der Brieftext: ein Textfeld mit vier Knöpfen.** Fett, kursiv,
 * Aufzählung, nummerierte Liste — die Teilmenge, die das PDF kennt. Die
 * Vorschau ist das PDF selbst: Ein zweiter Markdown-Renderer im Browser wäre
 * die erste Abweichung zwischen Vorschau und Beleg (19.2-44).
 */
export function MarkdownField({ value, onChange, disabled, rows = 10, className = '' }: Props) {
  const { t } = useTranslation()
  const feld = useRef<HTMLTextAreaElement>(null)

  const setzeAuswahl = (start: number, ende: number) =>
    requestAnimationFrame(() => {
      feld.current?.focus()
      feld.current?.setSelectionRange(start, ende)
    })

  /** Markierung einrahmen — ohne Markierung ein Platzhalterwort, damit das Ergebnis sichtbar ist. */
  const umschliesse = (zeichen: string) => {
    const el = feld.current
    if (!el) return
    const { selectionStart: s, selectionEnd: e } = el
    const text = value.slice(s, e) || t('documents.markdown.text')
    onChange(`${value.slice(0, s)}${zeichen}${text}${zeichen}${value.slice(e)}`)
    setzeAuswahl(s + zeichen.length, s + zeichen.length + text.length)
  }

  /** Jede markierte Zeile als Listenpunkt — ganze Zeilen, auch wenn nur ein Teil markiert ist. */
  const alsListe = (nummeriert: boolean) => {
    const el = feld.current
    if (!el) return
    const anfang = value.lastIndexOf('\n', el.selectionStart - 1) + 1
    const endeRoh = value.indexOf('\n', el.selectionEnd)
    const ende = endeRoh === -1 ? value.length : endeRoh
    const zeilen = value.slice(anfang, ende).split('\n')
    const neu = zeilen
      .map((z, i) => `${nummeriert ? `${i + 1}. ` : '- '}${z.replace(/^\s*(?:[-•]|\d+[.)])\s+/, '')}`)
      .join('\n')
    onChange(`${value.slice(0, anfang)}${neu}${value.slice(ende)}`)
    setzeAuswahl(anfang, anfang + neu.length)
  }

  const knopf = 'rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40'
  return (
    <div className="space-y-1">
      <div className="flex gap-1">
        <button type="button" disabled={disabled} onClick={() => umschliesse('**')} title={t('documents.markdown.bold')} className={knopf}>
          <Bold className="h-4 w-4" />
        </button>
        <button type="button" disabled={disabled} onClick={() => umschliesse('*')} title={t('documents.markdown.italic')} className={knopf}>
          <Italic className="h-4 w-4" />
        </button>
        <button type="button" disabled={disabled} onClick={() => alsListe(false)} title={t('documents.markdown.list')} className={knopf}>
          <List className="h-4 w-4" />
        </button>
        <button type="button" disabled={disabled} onClick={() => alsListe(true)} title={t('documents.markdown.ordered')} className={knopf}>
          <ListOrdered className="h-4 w-4" />
        </button>
      </div>
      <textarea ref={feld} disabled={disabled} rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className={`${className} font-mono`} />
    </div>
  )
}
